import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, UtensilsCrossed, Pill, Dumbbell, Activity, Users, MessageCircle, LogOut, PawPrint, Syringe } from "lucide-react";
import { api } from "@/lib/api";
import Today from "@/pages/bitepal/sections/Today";
import Diary from "@/pages/bitepal/sections/Diary";
import Meds from "@/pages/bitepal/sections/Meds";
import Injections from "@/pages/bitepal/sections/Injections";
import Fitness from "@/pages/bitepal/sections/Fitness";
import Vitals from "@/pages/bitepal/sections/Vitals";
import Community from "@/pages/bitepal/sections/Community";
import Coach from "@/pages/bitepal/sections/Coach";

const NAV = [
  { key: "today", label: "Today", icon: LayoutDashboard },
  { key: "diary", label: "Diary", icon: UtensilsCrossed },
  { key: "meds", label: "Meds", icon: Pill },
  { key: "injections", label: "Injections", icon: Syringe },
  { key: "fitness", label: "Fitness", icon: Dumbbell },
  { key: "vitals", label: "Vitals", icon: Activity },
  { key: "community", label: "Community", icon: Users },
];

export default function Home({ user }) {
  const navigate = useNavigate();
  const [section, setSection] = useState("today");
  const [coachOpen, setCoachOpen] = useState(false);

  const logout = async () => { try { await api("/auth/logout", { method: "POST" }); } catch {} navigate("/"); };

  const Section = { today: Today, diary: Diary, meds: Meds, injections: Injections, fitness: Fitness, vitals: Vitals, community: Community }[section];

  return (
    <div className="min-h-screen bg-[#F5F0E8] text-slate-900 flex">
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r border-[#E3D9C8] bg-[#ECE4D6] p-4 hidden md:flex flex-col">
        <div className="flex items-center gap-2 px-2 py-2">
          <div className="w-9 h-9 rounded-xl bg-[#651F33]/15 flex items-center justify-center"><PawPrint size={20} className="text-[#651F33]" /></div>
          <span className="text-xl font-extrabold">STILL</span>
        </div>
        <nav className="mt-6 flex flex-col gap-1">
          {NAV.map((n) => {
            const Icon = n.icon; const active = section === n.key;
            return (
              <button key={n.key} data-testid={`nav-${n.key}`} onClick={() => setSection(n.key)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${active ? "bg-[#651F33]/15 text-[#651F33]" : "text-slate-500 hover:bg-white"}`}>
                <Icon size={18} /> {n.label}
              </button>
            );
          })}
        </nav>
        <div className="mt-auto">
          <button data-testid="coach-open-button" onClick={() => setCoachOpen(true)}
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-bold text-[#A78BFA] bg-[#A78BFA]/12 border border-[#A78BFA]/30 hover:bg-[#A78BFA]/20">
            <MessageCircle size={18} /> Ask Bubba
          </button>
          <div className="mt-3 flex items-center gap-2 px-2">
            <div className="w-9 h-9 rounded-full bg-[#651F33] flex items-center justify-center text-[#F5F0E8] font-extrabold">{user?.name?.[0]?.toUpperCase() || "U"}</div>
            <div className="flex-1 min-w-0"><p className="text-sm font-bold truncate">{user?.name}</p><p className="text-[11px] text-slate-500 truncate">{user?.email}</p></div>
            <button data-testid="logout-button" onClick={logout} className="text-slate-500 hover:text-[#F43F5E]"><LogOut size={18} /></button>
          </div>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-[#ECE4D6] border-t border-[#E3D9C8] flex justify-around py-2">
        {NAV.map((n) => { const Icon = n.icon; const active = section === n.key;
          return <button key={n.key} onClick={() => setSection(n.key)} className={active ? "text-[#651F33]" : "text-slate-500"}><Icon size={22} /></button>; })}
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 max-w-5xl mx-auto w-full px-5 py-6 pb-24 md:pb-6">
        <div className="md:hidden flex items-center justify-between mb-4">
          <span className="text-xl font-extrabold">STILL</span>
          <button data-testid="coach-open-button-m" onClick={() => setCoachOpen(true)} className="w-10 h-10 rounded-full bg-[#A78BFA]/15 border border-[#A78BFA]/30 flex items-center justify-center"><MessageCircle size={18} className="text-[#A78BFA]" /></button>
        </div>
        <Section user={user} />
      </main>

      {coachOpen && <Coach onClose={() => setCoachOpen(false)} />}
    </div>
  );
}

export const Card = ({ children, className = "" }) => (
  <div className={`bg-white border border-[#E3D9C8] rounded-3xl p-5 ${className}`}>{children}</div>
);
